import {
  AssignmentProposalAction,
  AssignmentProposalErrorCode,
  AssignmentProposalStatus,
  type Link,
  type Maybe,
  type PreApproveAssignmentProposalInput,
  type SubmitAssignmentProposalInput,
} from "@models/ISchema";
import type {
  ActiveProposal,
  ApprovalFlowAbilities,
  OpenedProposalRequest,
  ProposalIntent,
} from "@services/types/assignment-proposal/assignmentProposalTypes";
import { resolveLinkProgrammingState } from "./programmingGroupState";

interface ProposalModeInput {
  isApprovalFlowEnabled: boolean;
  abilities?: ApprovalFlowAbilities | null;
}

/**
 * Whether the user can have proposals of their own: the flow is on and they take part in it and
 * send proposals, whether or not they can also incorporate them.
 */
export const canSubmitProposals = ({
  isApprovalFlowEnabled,
  abilities,
}: ProposalModeInput): boolean =>
  isApprovalFlowEnabled &&
  !!abilities?.can_participate_in_approval_flow &&
  !!abilities?.can_submit_proposals;

/**
 * Proposal mode on a regular entry: a requester who cannot incorporate. Everyone else keeps saving
 * directly, including a user who can send and incorporate.
 */
export const isProposalMode = (access: ProposalModeInput): boolean =>
  canSubmitProposals(access) && !access.abilities?.can_incorporate_proposals;

/** Whether the user reviews proposals: intermediate approval or incorporation (final reviewer). */
export const canReviewProposals = ({
  isApprovalFlowEnabled,
  abilities,
}: ProposalModeInput): boolean =>
  isApprovalFlowEnabled &&
  !!abilities?.can_participate_in_approval_flow &&
  (!!abilities?.can_pre_approve_proposals || !!abilities?.can_incorporate_proposals);

type BoundProposal = Pick<ActiveProposal, "mode" | "allowedActions" | "status"> | null | undefined;

/**
 * "Guardar como borrador" exists only for a proposal that was never sent: a new one or a draft. A
 * rejected proposal is corrected and resent, and a reviewer never saves drafts.
 */
export const canSaveDraft = (proposal: BoundProposal): boolean =>
  !proposal || proposal.mode === "draft";

const isReviewAllowing = (proposal: BoundProposal, action: AssignmentProposalAction): boolean =>
  proposal?.mode === "review" && !!proposal.allowedActions?.includes(action);

/** The editor reviews a proposal on which the user may give the intermediate approval. */
export const canPreApproveProposal = (proposal: BoundProposal): boolean =>
  isReviewAllowing(proposal, AssignmentProposalAction.PreApprove);

/** The editor reviews a proposal the user may reject. */
export const canRejectProposal = (proposal: BoundProposal): boolean =>
  isReviewAllowing(proposal, AssignmentProposalAction.Reject);

/** The editor reviews a proposal the user may apply to Darwin (final reviewer). */
export const canApplyProposal = (proposal: BoundProposal): boolean =>
  isReviewAllowing(proposal, AssignmentProposalAction.Apply);

/** A final reviewer is about to apply a proposal that has no intermediate approval yet. */
export const skipsIntermediateReview = (proposal: BoundProposal): boolean =>
  canApplyProposal(proposal) && proposal?.status === AssignmentProposalStatus.InReviewIntermediate;

/**
 * What confirming the validation does with the bound proposal. Applying wins over the intermediate
 * approval when the user may do both, since it covers that step too.
 */
export type ProposalDecisionKind = "apply" | "pre-approve" | "resend" | "send";

export const proposalDecisionKind = (proposal: BoundProposal): ProposalDecisionKind => {
  if (canApplyProposal(proposal)) return "apply";
  if (canPreApproveProposal(proposal)) return "pre-approve";

  return proposal?.mode === "rejected" ? "resend" : "send";
};

/** A link with microprogramming pending or applied does not admit proposals. */
export const isLinkClosedToProposals = (
  link: Maybe<Pick<Link, "groups" | "programmingStructure">> | undefined,
): boolean => !!link?.programmingStructure?.id || !!resolveLinkProgrammingState(link);

/**
 * The requester is proposing changes: a new proposal, a draft or the correction of a rejected one.
 * A sent proposal or one under review is not proposing anything.
 */
export const isProposing = (proposal: BoundProposal): boolean =>
  !proposal || proposal.mode === "draft" || proposal.mode === "rejected";

const isSameLink = (
  proposalLinkId: string | null | undefined,
  linkId: string | number | null | undefined,
): boolean => proposalLinkId != null && linkId != null && String(proposalLinkId) === String(linkId);

/** The active proposal when it belongs to the given link, else null. */
export const activeProposalOfLink = (
  activeProposal: ActiveProposal | null,
  linkId: string | number | null | undefined,
): ActiveProposal | null =>
  activeProposal && isSameLink(activeProposal.linkId, linkId) ? activeProposal : null;

/**
 * True when the editor of this link shows a proposal nobody may change from it: a sent one, or one
 * under review by a user who can neither give it the intermediate approval nor apply it.
 */
export const isLinkLockedByProposal = (
  activeProposal: ActiveProposal | null,
  linkId: string | number | null | undefined,
): boolean => {
  const proposal = activeProposalOfLink(activeProposal, linkId);

  if (proposal?.mode === "sent") return true;

  return (
    proposal?.mode === "review" && !canPreApproveProposal(proposal) && !canApplyProposal(proposal)
  );
};

interface EditorProposalState {
  activeProposal: ActiveProposal | null;
  openedProposalRequest: OpenedProposalRequest | null;
}

/**
 * True while the editor was opened for a proposal (requested or already applied). Such an editor
 * runs in proposal mode for anyone with a role in the flow, so its content never reaches a direct
 * save.
 */
export const hasOpenedProposal = (state: EditorProposalState): boolean =>
  !!state.openedProposalRequest || state.activeProposal?.origin === "drawer";

/** Query parameter of the assignation editor URL that names the proposal it is opened for. */
export const PROPOSAL_ID_PARAM = "proposalId";

/** Query parameter set when the editor is opened to review the proposal, not as its requester. */
export const REVIEW_PARAM = "review";

/** The proposal the editor URL was opened for, or null on a regular entry. */
export const getProposalIdFromSearch = (search: string | null | undefined): string | null =>
  new URLSearchParams(search ?? "").get(PROPOSAL_ID_PARAM) || null;

/** Whether the editor URL opens the proposal for its review. */
export const isReviewSearch = (search: string | null | undefined): boolean =>
  new URLSearchParams(search ?? "").get(REVIEW_PARAM) === "true";

const ERROR_KEY_PREFIX = "assignation-edit.proposal.errors";

/** Codes with a message of their own; the key of each message is the code itself. */
const CODES_WITH_MESSAGE: ReadonlySet<string> = new Set<AssignmentProposalErrorCode>([
  AssignmentProposalErrorCode.VersionControlDisabled,
  AssignmentProposalErrorCode.MissingIdentityToken,
  AssignmentProposalErrorCode.LinkNotFound,
  AssignmentProposalErrorCode.NoChanges,
  AssignmentProposalErrorCode.ReviewerRequired,
  AssignmentProposalErrorCode.ReviewerNotFound,
  AssignmentProposalErrorCode.ProposalSectionCreateNotAllowed,
  AssignmentProposalErrorCode.ProposalSessionSplitNotAllowed,
  AssignmentProposalErrorCode.LinkScheduleNotSupported,
  AssignmentProposalErrorCode.ProposalNotFound,
  AssignmentProposalErrorCode.ProposalNotEditable,
  AssignmentProposalErrorCode.ProposalNotAllowed,
  AssignmentProposalErrorCode.ProposalActionNotAllowed,
  AssignmentProposalErrorCode.ProposalDraftNotAllowed,
  AssignmentProposalErrorCode.RejectionReasonRequired,
]);

/**
 * i18n key of the message for a backend error code. Service failures (VERSION_CONTROL_UNAVAILABLE,
 * LINK_SCHEDULE_INVALID), codes the front does not know yet and transport failures use the generic
 * text of what the user was doing.
 */
export const proposalErrorMessageKey = (
  code: AssignmentProposalErrorCode | string | null | undefined,
  intent: ProposalIntent,
): string => {
  const key =
    code && CODES_WITH_MESSAGE.has(code)
      ? code
      : `GENERIC_${intent.toUpperCase().replace("-", "_")}`;

  return `${ERROR_KEY_PREFIX}.${key}`;
};

/** The message key of a failed outcome: its error code when the backend rejected the request. */
export const outcomeErrorKey = (
  outcome: { kind: string; errorCode?: string | null },
  intent: ProposalIntent,
): string =>
  proposalErrorMessageKey(outcome.kind === "rejected" ? outcome.errorCode : null, intent);

interface ProposalInputParams {
  linkId: string;
  changesets: SubmitAssignmentProposalInput["changesets"];
  draft: boolean;
  /** Proposal the editor is bound to: it is updated (or sent) instead of a new one being created. */
  proposalId?: string | null;
  reviewerUsernames?: string[];
  comment?: string | null;
}

/**
 * Input of submitAssignmentProposal. Optional keys are left out instead of sent empty, and
 * editionMode, dryRun and skipValidations are never part of it: the backend only accepts the
 * assignation mode and decides on its own whether the validations let the proposal through.
 */
export const buildProposalInput = ({
  linkId,
  changesets,
  draft,
  proposalId,
  reviewerUsernames,
  comment,
}: ProposalInputParams): SubmitAssignmentProposalInput => {
  const trimmedComment = comment?.trim();

  return {
    linkId,
    draft,
    changesets,
    ...(proposalId ? { proposalId } : {}),
    ...(reviewerUsernames?.length ? { reviewerUsernames } : {}),
    ...(trimmedComment ? { comment: trimmedComment } : {}),
  };
};

interface DecisionInputParams {
  linkId: string;
  proposalId: string;
  /**
   * Intermediate approval: the reviewer's adjustment, only when the editor changed since the
   * proposal was applied. Application: always, every change of the editor against the link.
   */
  changesets?: PreApproveAssignmentProposalInput["changesets"] | null;
  comment?: string | null;
}

/**
 * Input of preApproveAssignmentProposal and applyAssignmentProposal (same shape); optional keys are
 * left out instead of sent empty, and editionMode is never sent.
 */
export const buildDecisionInput = ({
  linkId,
  proposalId,
  changesets,
  comment,
}: DecisionInputParams): PreApproveAssignmentProposalInput => {
  const trimmedComment = comment?.trim();

  return {
    linkId,
    proposalId,
    ...(changesets ? { changesets } : {}),
    ...(trimmedComment ? { comment: trimmedComment } : {}),
  };
};
